/**
 * "SAIU UMA VERSÃO NOVA" — a falha que não é defeito.
 *
 * Quando o Cloudflare Pages publica, os chunks trocam de hash. A aba que já
 * estava aberta continua com o índice ANTIGO em memória: ao abrir uma tela
 * `lazy()`, ela pede `/assets/<Tela>-<hashVelho>.js`, que não existe mais.
 * O import rejeita, o React joga o erro no boundary mais próximo e o usuário lê
 * "Algo deu errado nesta tela" — uma acusação falsa contra o sistema.
 *
 * Medido pelo Ruddy (semana de 29/07): 13 registros em `app_errors`, TODOS
 * desta classe, nas telas mais mexidas. Zero 500 de verdade no período.
 *
 * ⚠️ MEDIDO NO AR, e muda o reconhecimento: um chunk que sumiu NÃO devolve 404.
 * O Pages não tem `_redirects` nem `404.html`, então cai no fallback de SPA e
 * responde **200 com `text/html`** (o index.html inteiro). Conferido em
 * 29/07 contra `m5group.pages.dev`. Por isso o padrão abaixo cobre as DUAS
 * famílias de mensagem: a de rede ("Failed to fetch dynamically imported
 * module", que foi a medida) e a de MIME ("expected a JavaScript module script
 * but the server responded with … text/html"), que é como Chrome e Safari
 * relatam quando o corpo chega mas não é módulo. Reconhecer só a primeira
 * deixaria metade dos navegadores fora do conserto.
 */

/** Chave da trava anti-laço. Exportada porque o teste confere que ela persiste. */
export const CHAVE_TRAVA = "versao-nova-recarregou-em";

/**
 * Janela em que uma segunda recarga é RECUSADA.
 *
 * 🔒 É esta constante que impede a recarga infinita — o risco de piorar o
 * defeito. Um laço se manifesta em segundos (recarrega → falha → recarrega);
 * um deploy seguinte, em minutos. Trinta segundos separa os dois com folga.
 *
 * ⚠️ DESVIO DECLARADO DO PEDIDO: o Ruddy pediu "no MÁXIMO uma vez por sessão".
 * Ao pé da letra, quem deixa a aba aberta o dia todo se cura no primeiro deploy
 * e apanha em todos os outros — e a semana até sexta é de publicar MUITO. A
 * janela atende o motivo declarado da trava (não virar laço) sem pagar esse
 * preço. Pra voltar ao literal, basta `Infinity` aqui: nada mais muda.
 */
export const JANELA_ANTI_LACO_MS = 30_000;

const PADRAO_VERSAO = new RegExp(
  [
    // rede/import (Chrome, Firefox, Edge)
    "failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "importing a module script failed",
    // MIME: o corpo chegou, mas veio HTML (o caso do fallback de SPA do Pages)
    "expected a javascript.{0,20}module script",
    "responded with a mime type",
    // pré-carregamento de CSS do Vite, mesma causa
    "unable to preload css",
  ].join("|"),
  "i",
);

/**
 * Este erro é "sua aba está velha" ou é defeito de verdade?
 *
 * Olha mensagem E nome: o `ChunkLoadError` chega sem texto reconhecível em
 * alguns navegadores. Na dúvida responde `false` — classificar bug real como
 * "versão nova" o esconderia atrás de um reload, que é o pior erro possível
 * aqui.
 */
export function ehErroDeVersaoNova(err: unknown): boolean {
  if (!err) return false;
  const e = err as { message?: unknown; name?: unknown };
  if (String(e.name ?? "") === "ChunkLoadError") return true;
  return PADRAO_VERSAO.test(String(e.message ?? err));
}

/** Já houve uma recarga automática nesta aba (dentro da janela)? */
export function jaRecarregouPorVersao(): boolean {
  try {
    const t = Number(sessionStorage.getItem(CHAVE_TRAVA) ?? 0);
    return t > 0 && Date.now() - t < JANELA_ANTI_LACO_MS;
  } catch {
    // Sem storage não há memória — e sem memória a recarga vira laço.
    return true;
  }
}

/**
 * Recarrega UMA vez. Devolve `false` (sem recarregar) quando a trava está
 * armada — aí quem chamou mostra a saída manual.
 *
 * A marca é gravada ANTES do `reload()` de propósito: ela precisa estar em
 * disco quando a aba voltar, senão a próxima falha recarrega de novo.
 */
export function recarregarUmaVez(): boolean {
  if (jaRecarregouPorVersao()) return false;
  try {
    sessionStorage.setItem(CHAVE_TRAVA, String(Date.now()));
  } catch {
    return false; // não consigo lembrar que recarreguei → não recarrego
  }
  window.location.reload();
  return true;
}

// ════════════════════════════════════════════════════════════════════════════
// 28/09/2026 — O IMPORT DE CLIQUE (achado da Propostas, com o Ruddy mandando a proposta da TVH)
//
// O que está acima cobre a tela `lazy()` que quebra ao RENDERIZAR (cai no boundary). Não cobre o
// `import()` disparado por um CLIQUE — Word da proposta (`docx/documento`), PDF, zip, planilha: ~12
// lugares. Ali o erro cai no `catch` do botão e o Ruddy lê "Failed to fetch dynamically imported module:
// https://m5group.pages.dev/assets/documento-BV1HrRXX.js" — a aba era de antes dos deploys do fim de semana.
// Duas camadas:
//   1. PREVENIR: ao TROCAR DE TELA, confere se saiu versão (o `index-<hash>.js` do index.html publicado
//      mudou). Se saiu, recarrega JÁ na tela nova — nada digitado se perde (a tela acabou de abrir), e o
//      clique seguinte encontra os chunks da versão certa. Uma ida ao index.html (~6 KB) no máximo por minuto.
//   2. SE ESCAPAR (a aba ficou numa tela só durante o deploy): o Vite avisa `vite:preloadError` ANTES do erro
//      chegar ao botão; o erro ganha nome `ChunkLoadError` e texto que diz o que fazer. NÃO recarrega sozinho
//      aqui: no meio de um formulário (texto digitado, arquivo anexado) a recarga apagaria o trabalho.
// ════════════════════════════════════════════════════════════════════════════

export const MENSAGEM_VERSAO_NOVA =
  "O sistema foi atualizado enquanto esta aba estava aberta. Recarregue a página (Ctrl+Shift+R) e tente de novo. O que já foi salvo continua lá.";

/** Renomeia o erro de chunk velho para a mensagem que diz o que fazer (e o boundary segue reconhecendo). */
export function marcarComoVersaoNova(err: unknown): void {
  if (!err || typeof err !== "object") return;
  try {
    (err as Error).name = "ChunkLoadError";
    (err as Error).message = MENSAGEM_VERSAO_NOVA;
  } catch {
    /* erro congelado: fica a mensagem original, que o ehErroDeVersaoNova também reconhece */
  }
}

const RE_BUNDLE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

/** O `index-<hash>.js` que um index.html aponta (null se não achar — aí não se conclui nada). */
export function bundleDoIndex(html: string): string | null {
  return html.match(RE_BUNDLE)?.[0] ?? null;
}

/** Saiu versão nova? Só diz SIM com as duas pontas conhecidas e diferentes — na dúvida, NÃO. */
export function saiuVersaoNova(carregado: string | null, publicado: string | null): boolean {
  return !!carregado && !!publicado && carregado !== publicado;
}

/** O bundle que ESTA aba carregou (o <script type=module> do index). */
export function bundleCarregado(doc: Document = document): string | null {
  for (const s of Array.from(doc.querySelectorAll<HTMLScriptElement>("script[type=module][src]"))) {
    const b = bundleDoIndex(new URL(s.src, "http://x").pathname);
    if (b) return b;
  }
  return null;
}

export const INTERVALO_CONFERE_VERSAO_MS = 60_000;
let ultimaConferencia = 0;

/**
 * Chamada a cada troca de tela. Se o index.html publicado aponta outro bundle, recarrega UMA vez (mesma
 * trava anti-laço de cima). Falha de rede, dev (sem bundle) ou dúvida → não faz nada.
 */
export async function conferirVersaoAoTrocarDeTela(agora = Date.now()): Promise<boolean> {
  if (agora - ultimaConferencia < INTERVALO_CONFERE_VERSAO_MS) return false;
  ultimaConferencia = agora;
  const carregado = bundleCarregado();
  if (!carregado) return false;
  try {
    const r = await fetch("/", { cache: "no-store", headers: { accept: "text/html" } });
    if (!r.ok) return false;
    if (!saiuVersaoNova(carregado, bundleDoIndex(await r.text()))) return false;
  } catch {
    return false;
  }
  return recarregarUmaVez();
}

/** Só para o teste zerar o intervalo entre casos. */
export function _zerarConferenciaDeVersao(): void {
  ultimaConferencia = 0;
}
